import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Button, Input, VStack, HStack, Text, IconButton, 
  ScaleFade, Avatar, Flex, Spinner, Card, Image, useColorModeValue, Icon
} from '@chakra-ui/react';
import { CloseIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { FaExpand, FaCompress, FaPaperclip } from 'react-icons/fa';
import { sendToGemini } from '../../services/api'; 

const TAGA_ORANGE = "#F68633";

export const AIChatWidget = ({ onOpenTopic }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, sender: 'bot', text: 'Olá! Sou o Jurandir. Como posso ajudar hoje?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // --- HOOKS DE COR ---
  const bgChat = useColorModeValue('white', 'gray.800');
  const bgUser = useColorModeValue(TAGA_ORANGE, 'orange.600');
  const bgBot = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const chatBodyBg = useColorModeValue('gray.50', 'gray.900');
  const inputBg = useColorModeValue('gray.100', 'gray.700');
  const suggestionHoverBg = useColorModeValue('orange.50', 'whiteAlpha.100');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages, isTyping, isOpen]);

  // Lidar com seleção de arquivo
  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Função segura para fechar
  const handleClose = () => {
      setIsOpen(false);
      setIsExpanded(false); // Reseta o expandir para o botão voltar a aparecer
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && !selectedFile) return;

    const userText = inputValue;
    const fileToSend = selectedFile;

    setInputValue('');
    setSelectedFile(null);

    setMessages(prev => [...prev, { 
        id: Date.now(), 
        sender: 'user', 
        text: userText,
        image: fileToSend ? URL.createObjectURL(fileToSend) : null 
    }]);
    
    setIsTyping(true);
    
    const response = await sendToGemini(userText, fileToSend);
    
    setIsTyping(false);

    if (response.success) {
        setMessages(prev => [...prev, { 
          id: Date.now() + 1, sender: 'bot', 
          text: response.data.text, 
          suggestion: response.data.suggestion 
        }]);
    } else {
        setMessages(prev => [...prev, { id: Date.now() + 1, sender: 'bot', text: "Tive um problema de conexão. Tente novamente." }]);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleSendMessage(); };

  // Definição de estilos dinâmicos para Expandido vs Normal
  const chatBoxStyles = isExpanded ? {
      position: "fixed",
      top: 0,
      left: 0,
      w: "100vw",
      h: "100vh",
      borderRadius: 0,
      zIndex: 10000, // Z-index altíssimo para cobrir tudo
      m: 0
  } : {
      position: "absolute",
      bottom: "70px",
      right: "0",
      w: "300px",
      h: "420px",
      borderRadius: "xl",
      zIndex: 9999
  };

  return (
    <Box position="fixed" bottom="20px" right="20px" zIndex={9999}>
      <ScaleFade initialScale={0.9} in={isOpen} unmountOnExit>
        <Box
          bg={bgChat} boxShadow="xl"
          overflow="hidden" display="flex" flexDirection="column"
          border="1px solid" borderColor={borderColor}
          {...chatBoxStyles}
        >
          {/* Header */}
          <Flex bg={TAGA_ORANGE} p={3} align="center" justify="space-between" color="white">
            <HStack spacing={3}>
              {/* Imagem Corrigida: Sem padding, preenchendo o circulo */}
              <Box bg="white" borderRadius="full" w="42px" h="42px" border="2px solid white" overflow="hidden">
                <Image src="/user.png" w="100%" h="100%" objectFit="cover" fallbackSrc="https://via.placeholder.com/42" />
              </Box>
              <Box lineHeight="1.2">
                <Text fontWeight="bold" fontSize="sm">Jurandir</Text>
                <Text fontSize="xs" opacity={0.9}>Especialista TAGA</Text>
              </Box>
            </HStack>
            <HStack>
                <IconButton 
                    icon={isExpanded ? <FaCompress /> : <FaExpand />} 
                    size="sm" variant="ghost" color="white" 
                    _hover={{ bg: 'whiteAlpha.300' }} 
                    onClick={() => setIsExpanded(!isExpanded)} 
                    aria-label={isExpanded ? "Minimizar" : "Expandir"}
                />
                <IconButton 
                    icon={<CloseIcon />} 
                    size="sm" variant="ghost" color="white" 
                    _hover={{ bg: 'whiteAlpha.300' }} 
                    onClick={handleClose} // Chama a função segura de fechar
                    aria-label="Fechar"
                />
            </HStack>
          </Flex>

          {/* Body */}
          <Box flex="1" overflowY="auto" p={3} bg={chatBodyBg}>
            <VStack spacing={3} align="stretch">
              {messages.map((msg) => (
                <Flex key={msg.id} justify={msg.sender === 'user' ? 'flex-end' : 'flex-start'}>
                  {msg.sender === 'bot' && (
                    <Avatar src="/user.png" size="xs" w="24px" h="24px" mr={1} mt={0.5} bg="transparent" />
                  )}
                  <Box maxW={isExpanded ? "60%" : "85%"}> {/* Ajusta largura max no fullscreen */}
                    
                    {/* Imagem Enviada */}
                    {msg.image && (
                        <Image src={msg.image} borderRadius="md" mb={1} maxH="200px" objectFit="contain" border="2px solid" borderColor={bgUser} />
                    )}
                    
                    {/* Texto */}
                    {msg.text && (
                        <Box
                        bg={msg.sender === 'user' ? bgUser : bgBot}
                        color={msg.sender === 'user' ? 'white' : textColor}
                        px={3} py={2} borderRadius="lg"
                        boxShadow="sm"
                        borderTopLeftRadius={msg.sender === 'bot' ? 0 : 'lg'}
                        borderTopRightRadius={msg.sender === 'user' ? 0 : 'lg'}
                        >
                            <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">{msg.text}</Text>
                        </Box>
                    )}
                    
                    {/* Sugestão de Tópico */}
                    {msg.suggestion && (
                      <Card 
                        mt={1} p={2} cursor="pointer" 
                        borderLeft="3px solid" borderLeftColor={TAGA_ORANGE}
                        _hover={{ bg: suggestionHoverBg }}
                        onClick={() => { 
                            if (onOpenTopic) {
                                onOpenTopic(msg.suggestion);
                                if(isExpanded) setIsExpanded(false); // Minimiza ao clicar no link
                            } 
                        }}
                      >
                        <Text fontSize="xs" fontWeight="bold" color={TAGA_ORANGE}>VER TÓPICO RELACIONADO</Text>
                        <Text fontSize="xs" fontWeight="bold" noOfLines={1}>{msg.suggestion.title}</Text>
                      </Card>
                    )}
                  </Box>
                </Flex>
              ))}
              {isTyping && (
                <Flex align="center" color="gray.500" ml={8}>
                  <Spinner size="xs" w="12px" h="12px" mr={1} color={TAGA_ORANGE} />
                  <Text fontSize="xs" fontStyle="italic">Jurandir digitando...</Text>
                </Flex>
              )}
              <div ref={messagesEndRef} />
            </VStack>
          </Box>

          {/* Footer */}
          <Box p={2} bg={bgChat} borderTop="1px solid" borderColor={borderColor}>
            {/* Preview de Anexo */}
            {selectedFile && (
                <Flex mb={2} p={2} bg="gray.100" borderRadius="md" align="center" justify="space-between">
                    {/* CORRIGIDO: Texto sempre escuro pois o BG é gray.100 */}
                    <Text fontSize="xs" noOfLines={1} maxW="200px" color="gray.800">
                        {selectedFile.name}
                    </Text>
                    <IconButton icon={<CloseIcon />} size="xs" variant="ghost" color="red.500" onClick={() => setSelectedFile(null)} />
                </Flex>
            )}
            
            <HStack spacing={2}>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                onChange={handleFileSelect}
                accept="image/*,application/pdf" 
              />
              
              <IconButton 
                icon={<Icon as={FaPaperclip} />} 
                size="sm" 
                variant="ghost" 
                color="gray.500" 
                onClick={() => fileInputRef.current.click()}
                aria-label="Anexar arquivo"
              />

              <Input 
                placeholder="Digite sua mensagem..." 
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                borderRadius="full" size="sm" height="36px"
                bg={inputBg}
                border="none"
                _focus={{ ring: 1, ringColor: TAGA_ORANGE }}
                fontSize="sm"
              />
              <IconButton 
                icon={<ArrowUpIcon />} bg={TAGA_ORANGE} color="white" 
                borderRadius="full" size="sm" w="36px" h="36px"
                _hover={{ bg: 'orange.600' }}
                onClick={handleSendMessage}
                isDisabled={(!inputValue.trim() && !selectedFile) || isTyping}
              />
            </HStack>
          </Box>
        </Box>
      </ScaleFade>

      {/* Botão Flutuante (Só aparece se não estiver expandido) */}
      {!isExpanded && (
          <Button
            w="50px" h="50px" borderRadius="full"
            bg={TAGA_ORANGE}
            boxShadow="lg"
            _hover={{ transform: 'scale(1.05)', bg: 'orange.600' }}
            _active={{ transform: 'scale(0.95)' }}
            onClick={() => setIsOpen(!isOpen)}
            transition="all 0.2s"
            border="2px solid white"
            p={0}
            overflow="hidden"
        >
            {isOpen ? (
                <CloseIcon w={4} h={4} color="white" />
            ) : (
                <Image 
                    src="/user.png" 
                    w="100%" 
                    h="100%" 
                    objectFit="cover" 
                    fallbackSrc="https://via.placeholder.com/50" 
                />
            )}
        </Button>
      )}
    </Box>
  );
};